import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, FileText } from 'lucide-react';

export default function TermsPage() {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Terms and Conditions',
      lastUpdate: 'Last updated: March 2026',
      sections: [
        {
          title: '1. Acceptance of Terms',
          content: `By accessing and using BidTrip ("the Platform"), you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you may not use our services.

BidTrip is a marketplace that connects tourists ("Users" or "Clients") with experience providers ("Providers" or "Agencies") in tourist destinations in Mexico, including but not limited to Playa del Carmen, Cancún, Tulum, and Los Cabos.`
        },
        {
          title: '2. Description of Services',
          content: `BidTrip operates as an intermediary platform that:
• Allows Users to create requests for experiences (yachts, tours, activities, parties, car rentals, lodging)
• Enables Providers to send offers in response to these requests
• Facilitates secure payment processing through Stripe
• Provides a 12-hour window for Users to receive and compare offers
• Offers AI-powered price suggestions based on market data
• Manages an escrow system to protect both parties`
        },
        {
          title: '3. User Accounts',
          content: `3.1. To use BidTrip, you must create an account providing accurate and complete information.

3.2. You are responsible for maintaining the confidentiality of your account credentials.

3.3. You must be at least 18 years old to create an account.

3.4. BidTrip reserves the right to suspend or terminate accounts that violate these terms.`
        },
        {
          title: '4. Booking Process',
          content: `4.1. Users create requests specifying category, location, date, time, and number of people.

4.2. Providers have access to view requests and submit offers within their service areas.

4.3. Users have 12 hours from the first offer to select a Provider.

4.4. Once an offer is accepted, the booking is created and payment is required to confirm.

4.5. Price suggestions provided by BidTrip are approximate references only. Final prices depend on each Provider's offer.`
        },
        {
          title: '5. Payments and Fees',
          content: `5.1. BidTrip charges a 15% service fee on each transaction (or 10% when a referral code is used).

5.2. Users can pay 50% as a deposit or 100% of the total amount.

5.3. All payments are processed securely through Stripe.

5.4. Provider earnings are held in escrow until the experience is marked as completed.

5.5. Prices displayed on the platform are in USD (US Dollars).`
        },
        {
          title: '6. Cancellations and Refunds',
          content: `6.1. For paid bookings that are cancelled:
• A 20% cancellation fee applies
• 10% goes to the Provider as compensation
• 10% goes to BidTrip for administrative costs
• 80% is refunded to the User

6.2. Cancellation requests must be made through the platform.

6.3. No-shows are not eligible for refunds.`
        },
        {
          title: '7. User Responsibilities',
          content: `Users agree to:
• Provide accurate information when creating requests
• Arrive on time to scheduled experiences
• Follow safety instructions provided by Providers
• Treat Providers and their staff with respect
• Not engage in illegal activities during experiences`
        },
        {
          title: '8. Limitation of Liability',
          content: `8.1. BidTrip acts solely as an intermediary between Users and Providers.

8.2. BidTrip is not responsible for:
• The quality of experiences provided by Providers
• Accidents or injuries during experiences
• Weather-related cancellations or changes
• Personal property loss or damage
• Provider unavailability or service failures

8.3. Users participate in experiences at their own risk.`
        },
        {
          title: '9. Intellectual Property',
          content: `9.1. All content on BidTrip, including logos, text, graphics, and software, is property of BidTrip or its licensors.

9.2. Users may not copy, reproduce, or distribute platform content without written permission.`
        },
        {
          title: '10. Modifications',
          content: `BidTrip reserves the right to modify these Terms and Conditions at any time. Users will be notified of significant changes via email or platform notification. Continued use of the platform after changes constitutes acceptance of the new terms.`
        },
        {
          title: '11. Governing Law',
          content: `These Terms and Conditions are governed by the laws of Mexico. Any disputes arising from the use of BidTrip will be resolved in the competent courts of Mexico.`
        },
        {
          title: '12. Contact',
          content: `For questions about these Terms and Conditions, contact us at:
• Email: legal@bidtrip.live
• Support: soporte@bidtrip.live`
        }
      ]
    },
    es: {
      title: 'Términos y Condiciones',
      lastUpdate: 'Última actualización: Marzo 2026',
      sections: [
        {
          title: '1. Aceptación de los Términos',
          content: `Al acceder y utilizar BidTrip ("la Plataforma"), aceptas estar sujeto a estos Términos y Condiciones. Si no estás de acuerdo con alguna parte de estos términos, no podrás utilizar nuestros servicios.

BidTrip es un marketplace que conecta turistas ("Usuarios" o "Clientes") con proveedores de experiencias ("Proveedores" o "Agencias") en destinos turísticos de México, incluyendo pero no limitado a Playa del Carmen, Cancún, Tulum y Los Cabos.`
        },
        {
          title: '2. Descripción de los Servicios',
          content: `BidTrip opera como una plataforma intermediaria que:
• Permite a los Usuarios crear solicitudes de experiencias (yates, tours, actividades, fiestas, renta de autos, hospedaje)
• Permite a los Proveedores enviar ofertas en respuesta a estas solicitudes
• Facilita el procesamiento seguro de pagos a través de Stripe
• Proporciona una ventana de 12 horas para que los Usuarios reciban y comparen ofertas
• Ofrece sugerencias de precios basadas en IA con datos del mercado
• Administra un sistema de escrow para proteger a ambas partes`
        },
        {
          title: '3. Cuentas de Usuario',
          content: `3.1. Para usar BidTrip, debes crear una cuenta proporcionando información precisa y completa.

3.2. Eres responsable de mantener la confidencialidad de tus credenciales de cuenta.

3.3. Debes tener al menos 18 años para crear una cuenta.

3.4. BidTrip se reserva el derecho de suspender o terminar cuentas que violen estos términos.`
        },
        {
          title: '4. Proceso de Reservación',
          content: `4.1. Los Usuarios crean solicitudes especificando categoría, ubicación, fecha, hora y número de personas.

4.2. Los Proveedores pueden ver las solicitudes y enviar ofertas dentro de sus áreas de servicio.

4.3. Los Usuarios tienen 12 horas desde la primera oferta para seleccionar un Proveedor.

4.4. Una vez aceptada una oferta, se crea la reservación y se requiere el pago para confirmar.

4.5. Las sugerencias de precio proporcionadas por BidTrip son solo referencias aproximadas. Los precios finales dependen de la oferta de cada Proveedor.`
        },
        {
          title: '5. Pagos y Tarifas',
          content: `5.1. BidTrip cobra una tarifa de servicio del 15% en cada transacción (o 10% cuando se usa un código de referido).

5.2. Los Usuarios pueden pagar el 50% como depósito o el 100% del monto total.

5.3. Todos los pagos se procesan de forma segura a través de Stripe.

5.4. Las ganancias de los Proveedores se mantienen en escrow hasta que la experiencia se marque como completada.

5.5. Los precios mostrados en la plataforma están en USD (Dólares estadounidenses).`
        },
        {
          title: '6. Cancelaciones y Reembolsos',
          content: `6.1. Para reservaciones pagadas que se cancelen:
• Se aplica una tarifa de cancelación del 20%
• 10% va al Proveedor como compensación
• 10% va a BidTrip por costos administrativos
• 80% se reembolsa al Usuario

6.2. Las solicitudes de cancelación deben hacerse a través de la plataforma.

6.3. Las ausencias (no-shows) no son elegibles para reembolso.`
        },
        {
          title: '7. Responsabilidades del Usuario',
          content: `Los Usuarios aceptan:
• Proporcionar información precisa al crear solicitudes
• Llegar a tiempo a las experiencias programadas
• Seguir las instrucciones de seguridad proporcionadas por los Proveedores
• Tratar a los Proveedores y su personal con respeto
• No participar en actividades ilegales durante las experiencias`
        },
        {
          title: '8. Limitación de Responsabilidad',
          content: `8.1. BidTrip actúa únicamente como intermediario entre Usuarios y Proveedores.

8.2. BidTrip no es responsable de:
• La calidad de las experiencias proporcionadas por los Proveedores
• Accidentes o lesiones durante las experiencias
• Cancelaciones o cambios relacionados con el clima
• Pérdida o daño de propiedad personal
• Indisponibilidad del Proveedor o fallas en el servicio

8.3. Los Usuarios participan en las experiencias bajo su propio riesgo.`
        },
        {
          title: '9. Propiedad Intelectual',
          content: `9.1. Todo el contenido en BidTrip, incluyendo logos, texto, gráficos y software, es propiedad de BidTrip o sus licenciantes.

9.2. Los Usuarios no pueden copiar, reproducir o distribuir el contenido de la plataforma sin permiso escrito.`
        },
        {
          title: '10. Modificaciones',
          content: `BidTrip se reserva el derecho de modificar estos Términos y Condiciones en cualquier momento. Los Usuarios serán notificados de cambios significativos por correo electrónico o notificación en la plataforma. El uso continuado de la plataforma después de los cambios constituye aceptación de los nuevos términos.`
        },
        {
          title: '11. Ley Aplicable',
          content: `Estos Términos y Condiciones se rigen por las leyes de México. Cualquier disputa que surja del uso de BidTrip será resuelta en los tribunales competentes de México.`
        },
        {
          title: '12. Contacto',
          content: `Para preguntas sobre estos Términos y Condiciones, contáctanos en:
• Correo: legal@bidtrip.live
• Soporte: soporte@bidtrip.live`
        }
      ]
    }
  };

  const t = content[language] || content.es;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="terms-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={24} />
          </Link>
          <div className="flex items-center gap-2">
            <FileText className="text-caribbean" size={24} />
            <h1 className="font-heading text-xl font-bold">{t.title}</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-10">
          {/* Logo */}
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl font-bold">
              Bid<span className="text-caribbean">Trip</span>
            </h2>
            <p className="text-sm text-muted-foreground mt-2">{t.lastUpdate}</p>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {t.sections.map((section, index) => (
              <section key={index} className="prose prose-slate max-w-none">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-3">
                  {section.title}
                </h3>
                <div className="text-muted-foreground whitespace-pre-line text-sm leading-relaxed">
                  {section.content}
                </div>
              </section>
            ))}
          </div>

          {/* Footer Links */}
          <div className="mt-12 pt-8 border-t flex flex-wrap gap-4 justify-center text-sm">
            <Link to="/privacy" className="text-caribbean hover:underline">
              {language === 'en' ? 'Privacy Policy' : 'Política de Privacidad'}
            </Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/provider-terms" className="text-caribbean hover:underline">
              {language === 'en' ? 'Provider Terms' : 'Términos para Proveedores'}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
